import { Component, inject, OnInit, signal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ToastService } from '@app/core/services/toast.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { MailVerificationDialogComponent } from './mail-verification-dialog/mail-verification-dialog.component';
import { confirmedEqualValidator } from '@app/shared/form-validators';
import { UsersService } from 'api-v3/services';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-change-email',
    templateUrl: './change-email.component.html',
    styleUrls: ['./change-email.component.scss'],
    imports: [
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatError,
        MatButton,
        TranslateModule,
    ],
})
export class ChangeEmailComponent implements OnInit {
    private dialog = inject(MatDialog);
    private usersService = inject(UsersService);
    private toastService = inject(ToastService);
    private translateService = inject(TranslateService);
    private formBuilder = inject(UntypedFormBuilder);

    userEmailsForm: UntypedFormGroup;
    isLoading = signal(false);
    hasExternalIdp: boolean;

    constructor() {
        // check if the user has an external IdP (false if there is no organization or session)
        const session = inject(SessionService).getSession();
        const org = session?.organizations[0];
        this.hasExternalIdp = (org?.externalIdps?.length ?? 0) > 0;
    }

    ngOnInit() {
        this.userEmailsForm = this.formBuilder.group(
            {
                newEmail: new UntypedFormControl('', [
                    Validators.required,
                    Validators.email,
                ]),
                newEmailRepeat: new UntypedFormControl('', [
                    Validators.required,
                    Validators.email,
                ]),
            },
            {
                validators: [
                    confirmedEqualValidator('newEmail', 'newEmailRepeat'),
                ],
            }
        );
    }

    async openCodeVerification() {
        try {
            this.isLoading.set(true);
            await lastValueFrom(
                this.usersService.requestEmailChangeV3({
                    body: {
                        newEmail: this.userEmailsForm.value['newEmail'],
                    },
                })
            );
            this.isLoading.set(false);

            const dialogRef = this.dialog.open(
                MailVerificationDialogComponent,
                {
                    disableClose: false,
                }
            );
            const result = await lastValueFrom(dialogRef.afterClosed());
            if (result) this.userEmailsForm.reset();
        } catch (e: any) {
            this.isLoading.set(false);
            if (e.status === 401) {
                this.toastService.error(
                    this.translateService.instant('GLOBAL.ERROR.UNAUTHORIZED')
                );
            } else {
                this.toastService.error(
                    this.translateService.instant('ERRORS.GENERAL')
                );
            }
        }
    }

    resetForm() {
        this.userEmailsForm.reset();
        this.userEmailsForm.get('newEmail')?.setErrors(null);
        this.userEmailsForm.get('newEmailRepeat')?.setErrors(null);
    }

    //  getter for  access to form field
    get f() {
        return this.userEmailsForm.controls;
    }
}
